import { ImportTemplateIcon } from "@images/svgIcons/importTemplateIcon"
import { useProgrammationSessionImportSidebar } from "@modules/boxB2BProgram/hooks/useProgrammationSessionImportSidebar"
import { BoxProgrammingWeekCopyModal } from "@modules/boxPlanning/components/boxProgrammingHeader/boxProgrammingWeekCopyModal"
import { useBoxProgrammationSessionDuplicate } from "@modules/boxPlanning/hooks/useBoxProgrammationSessionDuplicate"
import { BoxProgrammation } from "@modules/boxProgrammation/boxProgrammationTypes"
import { useBoxScheduledSessionsInRange } from "@modules/boxSlot/hooks/useBoxSlotSessionScheduled"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { ProgramWeekCopyModalRef } from "@modules/programs/components/programHeader/programWeekCopyModal"
import { HUButton } from "@modules/ui/components/huButton"
import { HUButtonIconProps } from "@modules/ui/components/huButtonIcon"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import dayjs from "dayjs"
import { useMemo, useRef } from "react"
import { useTheme } from "styled-components"

type BoxProgrammingHeaederActionsViewProps = {
  boxProgrammation: BoxProgrammation
}

export const BoxProgrammingHeaederActionsView: React.FC<BoxProgrammingHeaederActionsViewProps> = ({
  boxProgrammation,
}) => {
  const theme = useTheme()
  const toast = useToast()
  const { sessions: scheduledSessions } = useBoxScheduledSessionsInRange()
  const { currentWeekdays, selectedDate } = useCalendar()
  const { navigateToImportProgramSelection } = useProgrammationSessionImportSidebar()
  const { onCopyBoxProgrammationSessionWeek, weekCopied } = useBoxProgrammationSessionDuplicate()

  const programWeekCopyModalRef = useRef<ProgramWeekCopyModalRef>(null)

  const currentWeekHasSessions = scheduledSessions.length > 0
  const canCopyWeek = currentWeekHasSessions
  const canPasteWeek = useMemo(() => {
    if (!!weekCopied) {
      if (dayjs(selectedDate).weekday(6).isAfter()) {
        return (
          !dayjs(weekCopied.originStartDate).isSame(selectedDate, "week") ||
          boxProgrammation.id !== weekCopied.originProgramId
        )
      }
    }
    return false
  }, [weekCopied, boxProgrammation, selectedDate])

  const { firstDayOfWeek, lastDayOfWeek } = useMemo(() => {
    if (
      currentWeekdays &&
      currentWeekdays.length > 0 &&
      currentWeekdays[0].date &&
      currentWeekdays[currentWeekdays.length - 1].date
    )
      return {
        firstDayOfWeek: currentWeekdays[0].date.format("YYYY-MM-DD"),
        lastDayOfWeek: currentWeekdays[currentWeekdays.length - 1].date?.format("YYYY-MM-DD"),
      }
    else return {}
  }, [currentWeekdays])

  const onCopyWeekClick = () => {
    onCopyBoxProgrammationSessionWeek(
      boxProgrammation.id,
      boxProgrammation.name,
      dayjs(selectedDate).weekday(0).toISOString(),
      dayjs(selectedDate).weekday(6).toISOString(),
    )
  }

  const onPasteWeekClick = async () => {
    await programWeekCopyModalRef.current?.onPasteWeekClick()
  }

  const onImportButtonClick = () => {
    if (firstDayOfWeek && lastDayOfWeek)
      navigateToImportProgramSelection(boxProgrammation.id, firstDayOfWeek, lastDayOfWeek)
    else toast.show({ severity: "error", summary: word("global.error.default") })
  }

  return (
    <div className="px-2 gap-2 flex flex-row align-items-center justify-content-between pb-1 w-full">
      {/* Copy week button*/}
      <ActionButton
        action={onCopyWeekClick}
        iconProps={{
          iconView: (
            <span
              className={`p-button-icon pi pi-copy`}
              style={{ color: canCopyWeek ? theme.buttonPrimaryTextColor : theme.neutralColor400 }}
            />
          ),
        }}
        buttonLablel={word("program.weekCopyPaste.copy.button")}
        disabled={!canCopyWeek}
        colorType={"Primary"}
      />

      {/* Paste week button*/}
      <ActionButton
        action={onPasteWeekClick}
        iconProps={{
          iconView: (
            <span
              className={`p-button-icon pi pi-file-import`}
              style={{ color: canPasteWeek ? theme.buttonPrimaryTextColor : theme.neutralColor400 }}
            />
          ),
        }}
        buttonLablel={word("program.weekCopyPaste.paste.button")}
        disabled={!canPasteWeek}
        colorType={"Primary"}
      />

      {/* Import week button*/}
      <ActionButton
        action={onImportButtonClick}
        iconProps={{ iconView: <ImportTemplateIcon color={theme.textPrimary} /> }}
        buttonLablel={word("box.programmation.activity.importTemplate")}
        disabled={false}
        colorType={"Primary"}
      />

      <BoxProgrammingWeekCopyModal boxProgrammation={boxProgrammation} ref={programWeekCopyModalRef} />
    </div>
  )
}

type ActionButtonProps = {
  colorType: "Primary" | "Quaternary"
  action: () => void
  iconProps: HUButtonIconProps
  buttonLablel: string
  disabled: boolean
}
const ActionButton: React.FC<ActionButtonProps> = ({ colorType, action, iconProps, buttonLablel, disabled }) => {
  const theme = useTheme()

  return (
    <div className="flex flex-column align-items-center justify-content-between gap-1 text-center">
      <HUButton
        icon={iconProps}
        type="Rounded"
        colorType={colorType}
        size="XS"
        onClick={action}
        disabled={disabled}
        className={disabled ? "bg-transparent border-transparent" : ""}
      />

      <HUText fontStyle="LS" style={{ color: disabled ? theme.neutralColor400 : theme.buttonPrimaryTextColor }}>
        {buttonLablel}
      </HUText>
    </div>
  )
}
